import React, { useState } from 'react';
import SolutionCard from './SolutionCard';
import { AISolution } from '../../config/api';
import './SolutionsList.css';

export interface SolutionsListProps {
  solutions?: AISolution[];
  loading?: boolean;
  error?: string | null;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  showPagination?: boolean;
  showLoadMore?: boolean;
  showStats?: boolean;
  sortBy?: 'name' | 'company' | 'price' | 'featured' | 'created_at';
  sortOrder?: 'asc' | 'desc';
  onPageChange?: (page: number) => void;
  onLoadMore?: () => void;
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  onSolutionView?: (solution: AISolution) => void;
  onSolutionDemo?: (solution: AISolution) => void;
  onSolutionWebsite?: (solution: AISolution) => void;
  className?: string;
}

const SolutionsList: React.FC<SolutionsListProps> = ({
  solutions = [],
  loading = false,
  error = null,
  totalCount = 0,
  currentPage = 1,
  pageSize = 12,
  hasNextPage = false,
  hasPreviousPage = false,
  showPagination = true,
  showLoadMore = false,
  showStats = true,
  sortBy = 'featured',
  sortOrder = 'desc',
  onPageChange,
  onLoadMore,
  onSortChange,
  onSolutionView,
  onSolutionDemo,
  onSolutionWebsite,
  className = '',
}) => {
  const [localSortBy, setLocalSortBy] = useState(sortBy);
  const [localSortOrder, setLocalSortOrder] = useState(sortOrder);

  // Handle sort changes
  const handleSortChange = (newSortBy: string) => {
    let newSortOrder: 'asc' | 'desc' = 'desc';
    
    // If clicking the same sort field, toggle order
    if (newSortBy === localSortBy) {
      newSortOrder = localSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Default sort orders for different fields
      switch (newSortBy) {
        case 'name':
        case 'company':
          newSortOrder = 'asc';
          break;
        case 'price':
          newSortOrder = 'asc';
          break;
        case 'featured':
        case 'created_at':
        default:
          newSortOrder = 'desc';
          break;
      }
    }

    setLocalSortBy(newSortBy as 'name' | 'company' | 'price' | 'featured' | 'created_at');
    setLocalSortOrder(newSortOrder);
    
    if (onSortChange) {
      onSortChange(newSortBy, newSortOrder);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    if (onPageChange && page !== currentPage) {
      onPageChange(page);
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    if (onLoadMore && hasNextPage && !loading) {
      onLoadMore();
    }
  };

  // Calculate pagination info
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  const totalPages = Math.ceil(totalCount / pageSize);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className={`solutions-list ${className}`}>
      {/* Header with Stats and Sort */}
      {(showStats || onSortChange) && (
        <header className="solutions-list__header">
          {/* Stats */}
          {showStats && (
            <div className="solutions-list__stats">
              {loading ? (
                <span className="solutions-list__stats-loading">Loading solutions...</span>
              ) : error ? (
                <span className="solutions-list__stats-error">Error loading solutions</span>
              ) : (
                <span className="solutions-list__stats-text">
                  {totalCount > 0 ? (
                    <>
                      Showing {startItem}-{endItem} of {totalCount} solutions
                    </>
                  ) : (
                    'No solutions found'
                  )}
                </span>
              )}
            </div>
          )}

          {/* Sort Controls */}
          {onSortChange && !loading && !error && solutions.length > 0 && (
            <div className="solutions-list__sort">
              <label className="solutions-list__sort-label">Sort by:</label>
              <div className="solutions-list__sort-buttons">
                <button
                  className={`solutions-list__sort-button ${localSortBy === 'featured' ? 'solutions-list__sort-button--active' : ''}`}
                  onClick={() => handleSortChange('featured')}
                >
                  Featured
                  {localSortBy === 'featured' && (
                    <svg
                      className={`solutions-list__sort-icon ${localSortOrder === 'desc' ? 'solutions-list__sort-icon--desc' : ''}`}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  )}
                </button>
                
                <button
                  className={`solutions-list__sort-button ${localSortBy === 'name' ? 'solutions-list__sort-button--active' : ''}`}
                  onClick={() => handleSortChange('name')}
                >
                  Name
                  {localSortBy === 'name' && (
                    <svg
                      className={`solutions-list__sort-icon ${localSortOrder === 'desc' ? 'solutions-list__sort-icon--desc' : ''}`}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  )}
                </button>
                
                <button
                  className={`solutions-list__sort-button ${localSortBy === 'price' ? 'solutions-list__sort-button--active' : ''}`}
                  onClick={() => handleSortChange('price')}
                >
                  Price
                  {localSortBy === 'price' && (
                    <svg
                      className={`solutions-list__sort-icon ${localSortOrder === 'desc' ? 'solutions-list__sort-icon--desc' : ''}`}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  )}
                </button>
                
                <button
                  className={`solutions-list__sort-button ${localSortBy === 'company' ? 'solutions-list__sort-button--active' : ''}`}
                  onClick={() => handleSortChange('company')}
                >
                  Company
                  {localSortBy === 'company' && (
                    <svg
                      className={`solutions-list__sort-icon ${localSortOrder === 'desc' ? 'solutions-list__sort-icon--desc' : ''}`}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </header>
      )}

      {/* Error State */}
      {error && (
        <div className="solutions-list__error">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3>Error Loading Solutions</h3>
          <p>{error}</p>
          <button 
            className="solutions-list__retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && solutions.length === 0 && (
        <div className="solutions-list__loading">
          <div className="solutions-list__loading-grid">
            {Array.from({ length: pageSize }, (_, index) => (
              <div key={index} className="solutions-list__loading-card">
                <div className="solutions-list__loading-header">
                  <div className="solutions-list__loading-logo"></div>
                  <div className="solutions-list__loading-title"></div>
                </div>
                <div className="solutions-list__loading-content">
                  <div className="solutions-list__loading-line"></div>
                  <div className="solutions-list__loading-line"></div>
                  <div className="solutions-list__loading-line solutions-list__loading-line--short"></div>
                </div>
                <div className="solutions-list__loading-features">
                  <div className="solutions-list__loading-feature"></div>
                  <div className="solutions-list__loading-feature"></div>
                  <div className="solutions-list__loading-feature"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Solutions Grid */}
      {!error && solutions.length > 0 && (
        <div className="solutions-list__grid">
          {solutions.map((solution) => (
            <SolutionCard
              key={solution.id}
              solution={solution}
              onViewClick={onSolutionView}
              onDemoClick={onSolutionDemo}
              onWebsiteClick={onSolutionWebsite}
            />
          ))}
          
          {/* Loading more cards */}
          {loading && solutions.length > 0 && (
            <>
              {Array.from({ length: 3 }, (_, index) => (
                <div key={`loading-${index}`} className="solutions-list__loading-card">
                  <div className="solutions-list__loading-header">
                    <div className="solutions-list__loading-logo"></div>
                    <div className="solutions-list__loading-title"></div>
                  </div>
                  <div className="solutions-list__loading-content">
                    <div className="solutions-list__loading-line"></div>
                    <div className="solutions-list__loading-line"></div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && solutions.length === 0 && (
        <div className="solutions-list__empty">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <h3>No Solutions Found</h3>
          <p>Try adjusting your search criteria or browse all solutions.</p>
        </div>
      )}

      {/* Load More Button */}
      {showLoadMore && !showPagination && hasNextPage && !loading && !error && (
        <div className="solutions-list__load-more">
          <button
            className="solutions-list__load-more-button"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg
                  className="solutions-list__loading-spinner"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Loading...
              </>
            ) : (
              'Load More Solutions'
            )}
          </button>
        </div>
      )}

      {/* Pagination */}
      {showPagination && !showLoadMore && totalPages > 1 && !loading && !error && (
        <nav className="solutions-list__pagination">
          <button
            className="solutions-list__pagination-button solutions-list__pagination-button--prev"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPreviousPage}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            Previous
          </button>

          <div className="solutions-list__pagination-pages">
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="solutions-list__pagination-ellipsis">...</span>
                ) : (
                  <button
                    className={`solutions-list__pagination-page ${page === currentPage ? 'solutions-list__pagination-page--active' : ''}`}
                    onClick={() => handlePageChange(page as number)}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <button
            className="solutions-list__pagination-button solutions-list__pagination-button--next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNextPage}
          >
            Next
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9,18 15,12 9,6" />
            </svg>
          </button>
        </nav>
      )}
    </div>
  );
};

export default SolutionsList;
